import React, { useState, useEffect, useCallback, useMemo } from 'react';

// קומפוננטת קלט - עכשיו בשפת Organic Luxury (מוצק, נקי, ללא זכוכית עכורה)
const Input = React.memo(({
                              label,
                              error,
                              value,
                              onChange,
                              onBlur,
                              name,
                              placeholder,
                              type = "text",
                              required = true
                          }) => {
    const inputId = `input-${name}`;

    return (
        <div className="mb-6">
            <label
                htmlFor={inputId}
                className="block text-text-primary font-medium mb-2 text-right text-base md:text-lg transition-colors duration-300"
            >
                {label}
                {required && <span className="text-accent mr-1">*</span>}
            </label>
            <input
                id={inputId}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                className={`
                    w-full p-4 rounded-xl text-base md:text-lg transition-all duration-300
                    placeholder-text-muted bg-background outline-none
                    border
                    ${error
                    ? 'border-red-500 focus:ring-2 focus:ring-red-200'
                    : 'border-secondary-dark focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20'
                } 
                    hover:border-primary/50 shadow-sm
                    text-right
                `}
                dir="rtl"
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${inputId}-error` : undefined}
            />
            {error && (
                <p
                    id={`${inputId}-error`}
                    className="mt-2 text-sm text-red-500 text-right animate-fade-in"
                    role="alert"
                >
                    {error}
                </p>
            )}
        </div>
    );
});

// קומפוננטת טקסט - עיצוב אחיד עם הקלט
const TextArea = React.memo(({
                                 label,
                                 error,
                                 value,
                                 onChange,
                                 onBlur,
                                 name,
                                 placeholder,
                                 required = true
                             }) => {
    const textareaId = `textarea-${name}`;

    return (
        <div className="mb-6">
            <label
                htmlFor={textareaId}
                className="block text-text-primary font-medium mb-2 text-right text-base md:text-lg transition-colors duration-300"
            >
                {label}
                {required && <span className="text-accent mr-1">*</span>}
            </label>
            <textarea
                id={textareaId}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                rows={5}
                className={`
                    w-full p-4 rounded-xl text-base md:text-lg transition-all duration-300
                    placeholder-text-muted bg-background outline-none resize-none
                    border
                    ${error
                    ? 'border-red-500 focus:ring-2 focus:ring-red-200'
                    : 'border-secondary-dark focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20'
                }
                    hover:border-primary/50 shadow-sm
                    text-right leading-relaxed
                `}
                dir="rtl"
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${textareaId}-error` : undefined}
            />
            {error && (
                <p
                    id={`${textareaId}-error`}
                    className="mt-2 text-sm text-red-500 text-right animate-fade-in"
                    role="alert"
                >
                    {error}
                </p>
            )}
        </div>
    );
});

// קומפוננטת טעינה
const Loading = () => (
    <div className="flex items-center justify-center">
        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin mr-3" />
        <span>שולח...</span>
    </div>
);

// התראת הצלחה - הותאמה לצבעי המותג (ירוק-יער)
const SuccessNotification = ({ show, onClose }) => (
    <div className={`
        fixed top-24 left-4 z-50 max-w-sm w-full
        bg-primary text-white p-5 rounded-xl shadow-hover border border-primary-light
        transform transition-all duration-500 ease-out
        ${show ? 'translate-x-0 opacity-100' : '-translate-x-[150%] opacity-0'}
    `}>
        <div className="flex items-center justify-between">
            <p className="font-medium text-lg">בקשתך נשלחה בהצלחה!</p>
            <button
                onClick={onClose}
                className="text-white/70 hover:text-white transition-colors p-1"
                aria-label="סגור הודעה"
            >
                ✕
            </button>
        </div>
        <p className="text-sm mt-2 opacity-90 font-light">נחזור אליך בהקדם האפשרי עם פרטים נוספים.</p>
    </div>
);

const PersonalCustomization = () => {
    // הלוגיקה שלך נשמרה ב-100%
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        description: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [touched, setTouched] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);

    const validationRules = useMemo(() => ({
        name: {
            required: true,
            validate: (value) => {
                if (!value.trim()) return 'שם הוא שדה חובה';
                if (value.trim().length < 2) return 'שם חייב להכיל לפחות 2 תווים';
                return null;
            }
        },
        phone: {
            required: true,
            validate: (value) => {
                if (!value) return 'מספר טלפון הוא שדה חובה';
                const phoneRegex = /^[0-9]{9,10}$/;
                const cleanPhone = value.replace(/[\s-]/g, '');
                if (!phoneRegex.test(cleanPhone)) return 'מספר טלפון לא תקין (9-10 ספרות)';
                return null;
            }
        },
        description: {
            required: true,
            validate: (value) => {
                if (!value.trim()) return 'תיאור הבקשה הוא שדה חובה';
                if (value.trim().length < 10) return 'תיאור חייב להכיל לפחות 10 תווים';
                if (value.length > 1000) return 'תיאור ארוך מדי (מקסימום 1000 תווים)';
                return null;
            }
        }
    }), []);

    const validateField = useCallback((fieldName, value) => {
        const rule = validationRules[fieldName];
        if (!rule) return null;
        return rule.validate(value);
    }, [validationRules]);

    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;
        Object.keys(formData).forEach(field => {
            if (touched[field]) {
                const error = validateField(field, formData[field]);
                if (error) {
                    newErrors[field] = error;
                    isValid = false;
                }
            }
        });
        setErrors(newErrors);
        return isValid;
    }, [formData, touched, validateField]);

    useEffect(() => {
        if (Object.keys(touched).length > 0) {
            validateForm();
        }
    }, [formData, touched, validateForm]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleBlur = useCallback((field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    }, []);

    const contactConfig = useMemo(() => ({
        whatsapp: '+972559986085',
        email: 'bwdwtz417@gmail.com',
        subject: 'בקשה חדשה מהאתר - התאמה אישית'
    }), []);

    const sendMessage = useCallback((message) => {
        const isMobile = window.matchMedia("(max-width: 767px)").matches;
        if (isMobile) {
            const whatsappUrl = `https://wa.me/${contactConfig.whatsapp}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
        } else {
            const gmailUrl = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${encodeURIComponent(contactConfig.email)}&su=${encodeURIComponent(contactConfig.subject)}&body=${encodeURIComponent(message)}`;
            window.open(gmailUrl, '_blank');
        }
    }, [contactConfig]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const allTouched = Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {});
        setTouched(allTouched);

        const newErrors = {};
        let isValid = true;
        Object.keys(formData).forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) {
                newErrors[field] = error;
                isValid = false;
            }
        });
        setErrors(newErrors);

        if (!isValid) {
            const firstErrorField = Object.keys(newErrors)[0];
            const errorElement = document.getElementById(`input-${firstErrorField}`) ||
                document.getElementById(`textarea-${firstErrorField}`);
            errorElement?.focus();
            return;
        }

        setIsSubmitting(true);

        try {
            const message = `בקשה להתאמה אישית מהאתר pumawood.com

שם המזמין: ${formData.name}
מספר טלפון: ${formData.phone}
פרטי הבקשה: ${formData.description}

תאריך שליחה: ${new Date().toLocaleDateString('he-IL')}`;

            sendMessage(message);

            setFormData({ name: '', phone: '', description: '' });
            setTouched({});
            setErrors({});
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 5000);

        } catch (error) {
            console.error('Error sending form:', error);
            alert('אירעה שגיאה בשליחת הטופס. אנא נסה שוב.');
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, validateField, sendMessage]);

    const hasFormData = useMemo(() =>
            Object.values(formData).some(value => value.trim())
        , [formData]);

    return (
        // הוספתי pt-32 כדי שהטופס לא יסתתר מתחת ל-Navbar
        <div className="min-h-screen bg-background pt-10 pb-16 sm:pt-10 sm:pb-24 px-4 sm:px-6 lg:px-8 relative" dir="rtl">

            <SuccessNotification show={showSuccess} onClose={() => setShowSuccess(false)} />

            <div className="relative z-10 max-w-3xl mx-auto">
                {/* אזור כותרת - אדריכלי ונקי */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl sm:text-5xl font-medium text-text-primary mb-6 tracking-wide">
                        הזמנה בהתאמה אישית
                    </h1>
                    <div className="flex items-center justify-center mb-6">
                        <div className="w-16 h-1 bg-accent rounded-full" />
                    </div>
                    <p className="text-lg sm:text-xl text-text-secondary leading-relaxed max-w-2xl mx-auto font-light">
                        יש לכם רעיון ייחודי? נשמח לשמוע עליו ולתכנן יחד את המוצר המושלם עבורכם, מעץ מלא וברמת גימור ללא פשרות.
                    </p>
                </div>

                {/* מעטפת הטופס */}
                <div className="bg-surface rounded-2xl shadow-soft border border-secondary/50 p-6 sm:p-10 transition-all duration-300 hover:shadow-hover">
                    <form onSubmit={handleSubmit} noValidate>

                        <div className="mb-8 border-b border-secondary pb-4">
                            <h2 className="text-xl font-medium text-text-primary">פרטים אישיים</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                            <Input
                                label="שם מלא"
                                error={errors.name}
                                value={formData.name}
                                onChange={handleChange}
                                onBlur={() => handleBlur('name')}
                                name="name"
                                placeholder="הכנס את שמך המלא"
                                type="text"
                            />
                            <Input
                                label="מספר טלפון"
                                error={errors.phone}
                                value={formData.phone}
                                onChange={handleChange}
                                onBlur={() => handleBlur('phone')}
                                name="phone"
                                placeholder="05X-XXXXXXX"
                                type="tel"
                            />
                        </div>

                        <div className="mt-8 mb-8 border-b border-secondary pb-4">
                            <h2 className="text-xl font-medium text-text-primary">פרטי הפרויקט</h2>
                        </div>

                        <TextArea
                            label="תיאור הבקשה"
                            error={errors.description}
                            value={formData.description}
                            onChange={handleChange}
                            onBlur={() => handleBlur('description')}
                            name="description"
                            placeholder="סוג המוצר, מידות משוערות, צבעים וכל פרט שיעזור לנו להבין את החזון שלך..."
                        />
                        <div className="text-sm text-text-muted text-left mt-[-1rem] mb-6">
                            {formData.description.length}/1000 תווים
                        </div>

                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={isSubmitting || !hasFormData}
                                className={`
                                    w-full py-4 px-8 text-lg font-medium rounded-xl text-white
                                    transition-all duration-300 relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                                    ${(isSubmitting || !hasFormData)
                                    ? 'bg-secondary-dark text-white/70 cursor-not-allowed'
                                    : 'bg-primary hover:bg-primary-light shadow-md hover:shadow-lg'
                                }
                                `}
                            >
                                {isSubmitting ? <Loading /> : 'שלח בקשה להצעת מחיר'}
                            </button>
                            <p className="text-xs text-text-muted text-center mt-4">
                                * המידע מאובטח. נחזור אליך תוך יום עסקים אחד.
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PersonalCustomization;